import React from 'react';
import { CalendarClock, CheckCircle2, RotateCcw, X } from 'lucide-react';
import { cn } from '@hts/ui';
import { useGet, apiPost } from '../../hooks/useApi.js';
import { CardSkeleton } from '../../components/ui/Skeleton.js';
import { scoreColor, relativeTime } from '../../lib/format.js';
import type { FillProposalItem, FillProposalsResponse } from '../../api/types.js';

/**
 * Issue #71 — schedule-fill proposals on the dispatch board.
 *
 * Cards for open fill proposals (gap -> best unassigned ticket) with
 * Apply / Reject. Apply posts to /api/v1/dispatch-fill/:id/apply (dispatcher+);
 * Reject asks for a reason and posts to /:id/reject. No fabricated data:
 * when the fill engine is disabled or produced nothing, the section shows an
 * honest empty state.
 */
export function FillProposalsSection() {
  const { data, loading, refetch } = useGet<FillProposalsResponse>('/api/v1/dispatch-fill');
  const [busy, setBusy] = React.useState<Record<string, 'apply' | 'reject' | null>>({});
  const [rejecting, setRejecting] = React.useState<string | null>(null);
  const [rejectReason, setRejectReason] = React.useState('');
  const [done, setDone] = React.useState<Record<string, 'applied' | 'rejected'>>({});

  const items = data?.items ?? [];
  const pending = items.filter((p) => p.status === 'pending' && !done[p.id]);
  const applied = items.filter((p) => p.status === 'applied' || done[p.id] === 'applied');

  async function apply(id: string) {
    setBusy((prev) => ({ ...prev, [id]: 'apply' }));
    try {
      await apiPost(`/api/v1/dispatch-fill/${id}/apply`, {});
      setDone((prev) => ({ ...prev, [id]: 'applied' }));
    } finally {
      setBusy((prev) => ({ ...prev, [id]: null }));
    }
  }

  async function reject(id: string) {
    if (!rejectReason.trim()) return;
    setBusy((prev) => ({ ...prev, [id]: 'reject' }));
    try {
      await apiPost(`/api/v1/dispatch-fill/${id}/reject`, { reason: rejectReason.trim() });
      setDone((prev) => ({ ...prev, [id]: 'rejected' }));
      setRejecting(null);
      setRejectReason('');
    } finally {
      setBusy((prev) => ({ ...prev, [id]: null }));
    }
  }

  return (
    <section aria-label="Schedule fill proposals">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <CalendarClock className="h-4 w-4 text-[hsl(var(--content-subtle))]" />
          <h2 className="text-sm font-semibold text-[hsl(var(--content-subtle))] uppercase tracking-wide">
            Keep the Day Full — Fill Proposals
          </h2>
          {pending.length > 0 && (
            <span className="rounded-full bg-amber-100 dark:bg-amber-900/30 px-2 py-0.5 text-xs font-semibold text-amber-700 dark:text-amber-300">
              {pending.length}
            </span>
          )}
        </div>
        <button
          onClick={() => refetch()}
          className="rounded-lg border border-[hsl(var(--border))] px-2.5 py-1.5 text-xs text-[hsl(var(--content-subtle))] hover:bg-[hsl(var(--surface-subtle))] transition-colors flex items-center gap-1.5"
        >
          <RotateCcw className="h-3 w-3" /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {Array.from({ length: 2 }).map((_, i) => <CardSkeleton key={i} lines={4} />)}
        </div>
      ) : pending.length === 0 && applied.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[hsl(var(--border))] px-4 py-6 text-center">
          <p className="text-sm text-[hsl(var(--content-subtle))]">
            No schedule-fill proposals right now.
          </p>
          <p className="text-xs text-[hsl(var(--content-placeholder))] mt-1">
            The fill engine runs every 15 minutes for below-target technician days. Enable it in Settings (default off).
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {pending.map((item) => (
            <FillProposalCard
              key={item.id}
              item={item}
              busy={busy[item.id] ?? null}
              rejecting={rejecting === item.id}
              rejectReason={rejectReason}
              onRejectReasonChange={setRejectReason}
              onApply={() => apply(item.id)}
              onStartReject={() => { setRejecting(item.id); setRejectReason(''); }}
              onCancelReject={() => setRejecting(null)}
              onConfirmReject={() => reject(item.id)}
            />
          ))}
        </div>
      )}

      {applied.length > 0 && (
        <div className="mt-3 space-y-2">
          {applied.slice(0, 5).map((item) => (
            <div key={item.id} className="flex items-center gap-3 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-4 py-2.5">
              <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
              <span className="text-sm text-[hsl(var(--content))] flex-1 truncate">
                #{item.cwTicketId} {item.ticketSummary}
              </span>
              <span className="text-xs text-[hsl(var(--content-subtle))]">
                filled → {item.proposal?.techName ?? 'technician'}
              </span>
              <span className="text-xs text-[hsl(var(--content-placeholder))]">{relativeTime(item.createdAt)}</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function FillProposalCard({ item, busy, rejecting, rejectReason, onRejectReasonChange, onApply, onStartReject, onCancelReject, onConfirmReject }: {
  item: FillProposalItem;
  busy: 'apply' | 'reject' | null;
  rejecting: boolean;
  rejectReason: string;
  onRejectReasonChange: (v: string) => void;
  onApply: () => void;
  onStartReject: () => void;
  onCancelReject: () => void;
  onConfirmReject: () => void;
}) {
  const p = item.proposal;
  return (
    <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] shadow-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-[hsl(var(--border))] bg-[hsl(var(--surface-subtle))]">
        <div className="flex items-center gap-2">
          {item.proposal?.backlog && (
            <span className="rounded-full bg-slate-200 dark:bg-slate-700 px-2 py-0.5 text-[10px] font-semibold text-slate-600 dark:text-slate-300">
              Backlog fill
            </span>
          )}
          <span className="text-xs font-medium text-[hsl(var(--content))]">#{item.cwTicketId}</span>
          <span className="text-xs text-[hsl(var(--content-placeholder))]">{relativeTime(item.createdAt)}</span>
          {p && (
            <span className={cn('ml-auto text-xs font-bold tabular-nums', scoreColor(p.score))}>
              {(p.score * 100).toFixed(0)}% fit
            </span>
          )}
        </div>
        <p className="text-sm font-semibold text-[hsl(var(--content))] truncate mt-1">{item.ticketSummary}</p>
        <p className="text-xs text-[hsl(var(--content-subtle))] mt-0.5">
          {p ? (
            <>
              Fill <span className="font-medium text-[hsl(var(--content))]">{p.techName}</span>'s open gap
              {' '}{new Date(p.startsAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/Chicago' })}
              –{new Date(p.endsAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/Chicago' })}
            </>
          ) : (
            'Proposal details unavailable'
          )}
        </p>
      </div>

      <div className="px-4 py-3 space-y-2">
        <p className="text-xs text-[hsl(var(--content-subtle))]">{item.reason}</p>
        {p?.violatesSla && (
          <p className="text-xs text-amber-600 dark:text-amber-400" role="note">
            Placement is past this ticket's SLA deadline — will not auto-apply.
          </p>
        )}
        {!rejecting ? (
          <div className="flex items-center gap-2 pt-1">
            <button
              onClick={onApply}
              disabled={busy !== null}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 transition-colors"
            >
              {busy === 'apply' ? '…' : '✓ Apply'}
            </button>
            <button
              onClick={onStartReject}
              disabled={busy !== null}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border border-[hsl(var(--border))] text-[hsl(var(--content-subtle))] hover:bg-[hsl(var(--surface))] disabled:opacity-50 transition-colors"
            >
              <X className="h-3 w-3" /> Reject
            </button>
          </div>
        ) : (
          <div className="space-y-2 pt-1">
            <input
              autoFocus
              aria-label="Rejection reason"
              value={rejectReason}
              onChange={(e) => onRejectReasonChange(e.target.value)}
              placeholder="Why reject this fill? (required)"
              className="w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-3 py-1.5 text-xs text-[hsl(var(--content))] focus:outline-none focus:ring-1 focus:ring-[hsl(var(--border-strong))]"
            />
            <div className="flex items-center gap-2">
              <button
                onClick={onConfirmReject}
                disabled={!rejectReason.trim() || busy !== null}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {busy === 'reject' ? '…' : 'Confirm reject'}
              </button>
              <button
                onClick={onCancelReject}
                className="px-3 py-1.5 rounded-lg text-xs text-[hsl(var(--content-subtle))] hover:bg-[hsl(var(--surface-subtle))] transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
